import { useState, useRef, useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Mention from '@tiptap/extension-mention';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Bold, Italic, List, Heading2, Quote, Upload, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { postsAPI, uploadAPI, communitiesAPI } from '@/api/community';
import { useToast } from '@/hooks/use-toast';
import { getUserData, getAuthToken } from '@/utils/auth';
import { apiUrl } from '@/api/config';
import axios from 'axios';
import { trackEvent, attachUserProps } from '@/analytics/analytics';

export default function PostForm({ isOpen, onClose, onPostCreated, userType = 'student' }) {
  const [content, setContent] = useState('');
  const [postType, setPostType] = useState('regular_post');
  const [isPinned, setIsPinned] = useState(false);
  const [gradeId, setGradeId] = useState('');
  const [divisionId, setDivisionId] = useState('');
  const [communityId, setCommunityId] = useState('');
  const [attachments, setAttachments] = useState([]); // Store File objects until submit
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { toast } = useToast();
  const userData = getUserData('student') || getUserData('teacher') || getUserData('admin') || getUserData('guardian');

  // Autocomplete function for mentions
  const fetchMentionSuggestions = useCallback(async ({ query, type }) => {
    try {
      const token = getAuthToken('student') || getAuthToken('teacher') || getAuthToken('admin') || getAuthToken('guardian');
      const response = await axios.get(apiUrl('community/autocomplete/'), {
        params: { q: query, type },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data.results || [];
    } catch (error) {
      console.error('Error fetching autocomplete:', error);
      return [];
    }
  }, []);

  // Autocomplete function for hashtags
  const fetchHashtagSuggestions = useCallback(async ({ query }) => {
    try {
      const token = getAuthToken('student') || getAuthToken('teacher') || getAuthToken('admin') || getAuthToken('guardian');
      const response = await axios.get(apiUrl('community/hashtag-autocomplete/'), {
        params: { q: query },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data.results || [];
    } catch (error) {
      console.error('Error fetching hashtag autocomplete:', error);
      return [];
    }
  }, []);

  // TipTap Editor with Mention extension
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        // Explicitly exclude underline to prevent duplicate extension warning
        underline: false,
      }),
      Underline,
      Mention.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            mentionText: {
              default: null,
              parseHTML: element => element.getAttribute('data-mention-text'),
              renderHTML: attributes => {
                if (!attributes.mentionText) {
                  return {};
                }
                return {
                  'data-mention-text': attributes.mentionText,
                };
              },
            },
          };
        },
        renderText({ node }) {
          // Return the mention text in the format backend expects (e.g., "@grade 10" or "@community Math Club")
          // This is what gets inserted as plain text in the HTML output
          return node.attrs.mentionText || node.attrs.label || `@${node.attrs.id}`;
        },
      }).configure({
        HTMLAttributes: {
          class: 'mention bg-[hsl(var(--green-bright))]/20 text-[hsl(var(--green-bright))] px-1 rounded',
        },
        renderLabel({ node }) {
          // Return the label for display
          return node.attrs.label || node.attrs.id;
        },
        suggestion: {
          char: '@',
          allowedPrefixes: [' ', '\n'],
          items: async ({ query }) => {
            // Require at least 3 characters before showing suggestions
            const trimmedQuery = query.trim();
            if (trimmedQuery.length < 3) {
              return [];
            }
            
            // Convert query to lowercase (all mentions should be lowercase)
            const searchQuery = trimmedQuery.toLowerCase();
            
            // Always fetch grades, communities, and users for suggestions
            // Backend will handle filtering based on user permissions
            let actualQuery = searchQuery;
            
            // If query matches grade pattern exactly, prioritize grade
            // Otherwise, show all types
            const types = ['grade', 'community', 'user'];
            
            // Fetch results for all types in parallel
            const allResults = await Promise.all(
              types.map(type => fetchMentionSuggestions({ query: actualQuery, type }))
            );
            
            // Combine and deduplicate results
            const combinedResults = [];
            const seenIds = new Set();
            
            // Prioritize: grades, then communities, then users
            const typeOrder = ['grade', 'community', 'user'];
            for (const type of typeOrder) {
              const typeIndex = types.indexOf(type);
              if (typeIndex >= 0 && allResults[typeIndex]) {
                for (const item of allResults[typeIndex]) {
                  const key = `${item.type || type}_${item.id}`;
                  if (!seenIds.has(key)) {
                    seenIds.add(key);
                    combinedResults.push(item);
                  }
                }
              }
            }
            
            return combinedResults.map(item => ({
              id: item.id,
              label: item.label,
              // Store the full mention text (already in correct format from backend)
              mentionText: (item.value || item.label).toLowerCase(),
            }));
          },
          render: () => {
            let component;
            let popup;
            let currentProps = null;
            let currentEditor = null;
            let currentRange = null;

            return {
              onStart: (props) => {
                currentProps = props;
                currentEditor = props.editor;
                currentRange = props.range;
                component = document.createElement('div');
                component.className = 'mention-suggestion-list bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg shadow-lg max-h-60 overflow-y-auto z-50 min-w-[200px]';
                
                popup = tippy('body', {
                  getReferenceClientRect: props.clientRect,
                  appendTo: () => document.body,
                  content: component,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'bottom-start',
                });
              },
              
              onUpdate: (props) => {
                currentProps = props;
                currentEditor = props.editor;
                currentRange = props.range;
                const items = props.items;
                
                component.innerHTML = '';
                
                if (items.length === 0) {
                  const empty = document.createElement('div');
                  empty.className = 'p-2 text-sm text-[hsl(var(--muted-foreground))]';
                  empty.textContent = 'No results found';
                  component.appendChild(empty);
                  return;
                }
                
                items.forEach((item, index) => {
                  const itemElement = document.createElement('div');
                  itemElement.className = `p-2 cursor-pointer hover:bg-[hsl(var(--main-bg))] text-sm ${
                    index === props.selectedIndex ? 'bg-[hsl(var(--main-bg))]' : ''
                  }`;
                  itemElement.textContent = item.label;
                  
                  const handleSelect = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    
                    // Use props.select which will trigger the command function
                    // This is the proper TipTap way to handle selection
                    if (props && typeof props.select === 'function') {
                      props.select(index);
                    } else if (currentProps && typeof currentProps.select === 'function') {
                      currentProps.select(index);
                    } else {
                      // Fallback: directly call command function
                      const latestRange = props.range || currentRange;
                      const latestEditor = props.editor || currentEditor;
                      
                      if (latestEditor && latestRange) {
                        const mentionText = item.mentionText || item.label;
                        latestEditor
                          .chain()
                          .focus()
                          .insertContentAt(latestRange, [
                            {
                              type: 'mention',
                              attrs: {
                                id: String(item.id),
                                label: item.label,
                                mentionText: mentionText,
                              },
                            },
                            {
                              type: 'text',
                              text: ' ',
                            },
                          ])
                          .run();
                      }
                    }
                    
                    // Hide popup after a brief delay
                    setTimeout(() => {
                      if (popup && popup[0]) {
                        popup[0].hide();
                      }
                    }, 50);
                  };
                  
                  // Use mousedown to prevent editor from losing focus
                  itemElement.onmousedown = (e) => {
                    e.preventDefault();
                    handleSelect(e);
                  };
                  component.appendChild(itemElement);
                });
              },
              
              command: ({ editor, range, props: item }) => {
                // Insert the mention with proper attributes
                // Store mentionText in both attrs and as data attribute for HTML conversion
                // Ensure mention text is lowercase
                const mentionText = (item.mentionText || item.label || '').toLowerCase();
                
                // Ensure we have valid range and editor
                if (!range || !editor) {
                  console.error('Invalid range or editor in mention command');
                  return;
                }
                
                try {
                  editor
                    .chain()
                    .focus()
                    .insertContentAt(range, [
                      {
                        type: 'mention',
                        attrs: {
                          id: String(item.id),
                          label: item.label,
                          mentionText: mentionText,
                        },
                      },
                      {
                        type: 'text',
                        text: ' ',
                      },
                    ])
                    .run();
                } catch (error) {
                  console.error('Error inserting mention:', error);
                }
              },
              
              onKeyDown: (props) => {
                if (props.event.key === 'Escape') {
                  popup[0].hide();
                  return true;
                }
                return false;
              },
              
              onExit: () => {
                if (popup && popup[0]) {
                  popup[0].destroy();
                }
                if (component && component.parentNode) {
                  component.parentNode.removeChild(component);
                }
              },
            };
          },
        },
      }),
      // Hashtag extension using Mention (with different name to avoid conflicts)
      Mention.extend({
        name: 'hashtagMention',
        addAttributes() {
          return {
            ...this.parent?.(),
            hashtagText: {
              default: null,
              parseHTML: element => element.getAttribute('data-hashtag-text'),
              renderHTML: attributes => {
                if (!attributes.hashtagText) {
                  return {};
                }
                return {
                  'data-hashtag-text': attributes.hashtagText,
                };
              },
            },
          };
        },
        renderText({ node }) {
          return node.attrs.hashtagText || `#${node.attrs.id}`;
        },
      }).configure({
        HTMLAttributes: {
          class: 'hashtag bg-[hsl(var(--teal-vivid))]/20 text-[hsl(var(--teal-vivid))] px-1 rounded',
        },
        renderLabel({ node }) {
          return `#${node.attrs.label || node.attrs.id}`;
        },
        suggestion: {
          char: '#',
          allowedPrefixes: [' ', '\n'],
          items: async ({ query }) => {
            // Require at least 3 characters before showing suggestions
            const trimmedQuery = query.trim();
            if (trimmedQuery.length < 3) {
              return [];
            }
            
            const results = await fetchHashtagSuggestions({ query: trimmedQuery });
            return results.map(item => ({
              id: item.id,
              label: item.label,
              hashtagText: item.value || `#${item.label}`,
            }));
          },
          render: () => {
            let component;
            let popup;
            let currentProps = null;
            let currentEditor = null;
            let currentRange = null;

            return {
              onStart: (props) => {
                currentProps = props;
                currentEditor = props.editor;
                currentRange = props.range;
                component = document.createElement('div');
                component.className = 'hashtag-suggestion-list bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg shadow-lg max-h-60 overflow-y-auto z-50 min-w-[200px]';
                
                popup = tippy('body', {
                  getReferenceClientRect: props.clientRect,
                  appendTo: () => document.body,
                  content: component,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'bottom-start',
                });
              },
              
              onUpdate: (props) => {
                currentProps = props;
                currentEditor = props.editor;
                currentRange = props.range;
                const items = props.items;
                
                component.innerHTML = '';
                
                if (items.length === 0) {
                  const empty = document.createElement('div');
                  empty.className = 'p-2 text-sm text-[hsl(var(--muted-foreground))]';
                  empty.textContent = 'No results found';
                  component.appendChild(empty);
                  return;
                }
                
                items.forEach((item, index) => {
                  const itemElement = document.createElement('div');
                  itemElement.className = `p-2 cursor-pointer hover:bg-[hsl(var(--main-bg))] text-sm ${
                    index === props.selectedIndex ? 'bg-[hsl(var(--main-bg))]' : ''
                  }`;
                  itemElement.textContent = `#${item.label}`;
                  
                  const handleSelect = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    
                    // Use props.select which will trigger the command function
                    // This is the proper TipTap way to handle selection
                    if (props && typeof props.select === 'function') {
                      props.select(index);
                    } else if (currentProps && typeof currentProps.select === 'function') {
                      currentProps.select(index);
                    } else {
                      // Fallback: directly call command function
                      const latestRange = props.range || currentRange;
                      const latestEditor = props.editor || currentEditor;
                      
                      if (latestEditor && latestRange) {
                        const hashtagText = item.hashtagText || `#${item.label}`;
                        latestEditor
                          .chain()
                          .focus()
                          .insertContentAt(latestRange, [
                            {
                              type: 'hashtagMention',
                              attrs: {
                                id: String(item.id),
                                label: item.label,
                                hashtagText: hashtagText,
                              },
                            },
                            {
                              type: 'text',
                              text: ' ',
                            },
                          ])
                          .run();
                      }
                    }
                    
                    // Hide popup after a brief delay
                    setTimeout(() => {
                      if (popup && popup[0]) {
                        popup[0].hide();
                      }
                    }, 50);
                  };
                  
                  // Use mousedown to prevent editor from losing focus
                  itemElement.onmousedown = (e) => {
                    e.preventDefault();
                    handleSelect(e);
                  };
                  component.appendChild(itemElement);
                });
              },
              
              command: ({ editor, range, props: item }) => {
                const hashtagText = item.hashtagText || `#${item.label}`;
                editor
                  .chain()
                  .focus()
                  .insertContentAt(range, [
                    {
                      type: 'hashtagMention',
                      attrs: {
                        id: String(item.id),
                        label: item.label,
                        hashtagText: hashtagText,
                      },
                    },
                    {
                      type: 'text',
                      text: ' ',
                    },
                  ])
                  .run();
              },
              
              onKeyDown: (props) => {
                if (props.event.key === 'Escape') {
                  popup[0].hide();
                  return true;
                }
                return false;
              },
              
              onExit: () => {
                if (popup && popup[0]) {
                  popup[0].destroy();
                }
                if (component && component.parentNode) {
                  component.parentNode.removeChild(component);
                }
              },
            };
          },
        },
      }),
    ],
    content: '',
    onUpdate: ({ editor }) => {
      // Get HTML content - TipTap will use renderText to output plain text for mentions
      setContent(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm focus:outline-none min-h-[200px] p-4 max-w-none prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-em:text-foreground text-foreground',
      },
    },
  });

  // Load communities on mount
  useEffect(() => {
    if (isOpen && (userType === 'student' || userType === 'teacher' || userType === 'admin')) {
      loadCommunities();
    }
  }, [isOpen]);

  const loadCommunities = async () => {
    try {
      const response = await communitiesAPI.getCommunities();
      setCommunities(response.results || response || []);
    } catch (error) {
      console.error('Error loading communities:', error);
    }
  };

  const handleFileUpload = (files) => {
    // Just store the files locally, don't upload yet
    const newFiles = Array.from(files).map(file => ({
      file: file, // Store the File object
      filename: file.name,
      size: file.size,
      type: file.type,
    }));
    setAttachments(prev => [...prev, ...newFiles]);
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!content.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter post content',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    setUploading(true);
    try {
      // Upload files first
      const uploadedAttachments = [];
      if (attachments.length > 0) {
        const uploadPromises = attachments.map(async (attachment) => {
          const file = attachment.file;
          if (file.type.startsWith('image/')) {
            const result = await uploadAPI.uploadImage(file);
            return {
              type: file.type,
              url: result.url,
              thumbnail_url: result.thumbnail_url || result.url,
              filename: result.filename || file.name,
              size: result.size || file.size,
            };
          } else {
            const result = await uploadAPI.uploadFile(file);
            return {
              type: file.type,
              url: result.url,
              filename: result.filename || file.name,
              size: result.size || file.size,
            };
          }
        });
        uploadedAttachments.push(...await Promise.all(uploadPromises));
      }

      // Convert mention nodes to plain text format for backend parsing
      let processedContent = content;
      if (editor) {
        // Get HTML and convert mention spans to plain text
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;
        
        // Find all mention nodes (TipTap creates spans with data-type="mention")
        const mentions = tempDiv.querySelectorAll('span[data-type="mention"]');
        mentions.forEach((mention) => {
          // Get mentionText from data attributes
          // TipTap stores attrs as data-* attributes in HTML
          let mentionText = mention.getAttribute('data-mention-text') || 
                             mention.getAttribute('data-label') || 
                             mention.textContent?.trim() || 
                             `@${mention.getAttribute('data-id') || ''}`;
          
          // Ensure mention text is lowercase
          mentionText = mentionText.toLowerCase();
          
          // Replace the mention span with plain text
          const textNode = document.createTextNode(mentionText + ' ');
          if (mention.parentNode) {
            mention.parentNode.replaceChild(textNode, mention);
          }
        });
        
        // Find all hashtag nodes (TipTap creates spans with data-type="hashtagMention")
        const hashtags = tempDiv.querySelectorAll('span[data-type="hashtagMention"]');
        hashtags.forEach((hashtag) => {
          // Get hashtagText from data attributes
          const hashtagText = hashtag.getAttribute('data-hashtag-text') || 
                             hashtag.textContent?.trim() || 
                             `#${hashtag.getAttribute('data-id') || ''}`;
          
          // Replace the hashtag span with plain text
          const textNode = document.createTextNode(hashtagText + ' ');
          if (hashtag.parentNode) {
            hashtag.parentNode.replaceChild(textNode, hashtag);
          }
        });
        
        processedContent = tempDiv.innerHTML;
      }

      const postData = {
        content: processedContent,
        post_type: postType,
        attachments: uploadedAttachments.length > 0 ? uploadedAttachments : undefined,
        is_pinned: isPinned && (userType === 'admin'),
        grade_id: gradeId || undefined,
        division_id: divisionId || undefined,
        community_id: communityId || undefined,
      };

      await postsAPI.createPost(postData);
      
      // Reset form
      if (editor) {
        editor.commands.clearContent();
      }
      setContent('');
      setPostType('regular_post');
      setIsPinned(false);
      setGradeId('');
      setDivisionId('');
      setCommunityId('');
      setAttachments([]);

      toast({
        title: 'Success',
        description: 'Post created successfully',
      });

      try { trackEvent('community_post_created', attachUserProps({ post_type: postType, is_pinned: isPinned && (userType === 'admin'), community_id: communityId, attachments_count: uploadedAttachments.length })); } catch(e) {}

      onClose();
      if (onPostCreated) onPostCreated();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to create post',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  const canCreateAnnouncement = userType === 'admin' || userType === 'teacher';
  const canPin = userType === 'admin';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[hsl(var(--card-bg))] text-foreground [&>button]:text-foreground [&>button:hover]:bg-[hsl(var(--muted))]">
        <DialogHeader>
          <DialogTitle className="text-foreground">Create New Post</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Create a new post with mentions, hashtags, and attachments
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Post Type Selection */}
          {canCreateAnnouncement && (
            <div className="space-y-2">
              <Label className="text-foreground">Post Type</Label>
              <Select value={postType} onValueChange={setPostType}>
                <SelectTrigger className="bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))] text-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--popover-bg))] border-[hsl(var(--card-border))]">
                  <SelectItem value="regular_post" className="text-foreground hover:bg-[hsl(var(--muted))]">Regular Post</SelectItem>
                  <SelectItem value="announcement" className="text-foreground hover:bg-[hsl(var(--muted))]">Announcement</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Rich Text Editor */}
          <div className="space-y-2">
            <Label className="text-foreground">Content</Label>
            <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
              <CardContent className="p-0">
                {/* Toolbar */}
                {editor && (
                  <div className="border-b-[hsl(var(--card-border))] p-2 flex flex-wrap gap-1">
                    <button
                      onClick={() => editor.chain().focus().toggleBold().run()}
                      className={cn(
                        'p-2 rounded transition-colors text-foreground',
                        editor.isActive('bold')
                          ? 'bg-[hsl(var(--green-bright))] text-white'
                          : 'hover:bg-[hsl(var(--muted))]'
                      )}
                    >
                      <Bold className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => editor.chain().focus().toggleItalic().run()}
                      className={cn(
                        'p-2 rounded transition-colors text-foreground',
                        editor.isActive('italic')
                          ? 'bg-[hsl(var(--green-bright))] text-white'
                          : 'hover:bg-[hsl(var(--muted))]'
                      )}
                    >
                      <Italic className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                      className={cn(
                        'p-2 rounded transition-colors text-foreground',
                        editor.isActive('heading', { level: 2 })
                          ? 'bg-[hsl(var(--green-bright))] text-white'
                          : 'hover:bg-[hsl(var(--muted))]'
                      )}
                    >
                      <Heading2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => editor.chain().focus().toggleBulletList().run()}
                      className={cn(
                        'p-2 rounded transition-colors text-foreground',
                        editor.isActive('bulletList')
                          ? 'bg-[hsl(var(--green-bright))] text-white'
                          : 'hover:bg-[hsl(var(--muted))]'
                      )}
                    >
                      <List className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => editor.chain().focus().toggleBlockquote().run()}
                      className={cn(
                        'p-2 rounded transition-colors text-foreground',
                        editor.isActive('blockquote')
                          ? 'bg-[hsl(var(--green-bright))] text-white'
                          : 'hover:bg-[hsl(var(--muted))]'
                      )}
                    >
                      <Quote className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {/* Editor */}
                <div className="min-h-[200px]">
                  {editor && <EditorContent editor={editor} />}
                </div>
              </CardContent>
            </Card>
            <p className="text-xs text-muted-foreground">
              Type @ to mention: @grade9, @grade9a, @community_name, or @username (all lowercase). Use #hashtag for hashtags
            </p>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label className="text-foreground">Attachments</Label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
              onChange={(e) => handleFileUpload(e.target.files)}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="border-[hsl(var(--card-border))] text-foreground hover:bg-[hsl(var(--muted))]"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Files
                </>
              )}
            </Button>
            {attachments.length > 0 && (
              <div className="space-y-2 mt-2">
                {attachments.map((attachment, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-[hsl(var(--main-bg))] rounded border-[hsl(var(--card-border))]"
                  >
                    <span className="text-sm text-foreground truncate flex-1">
                      {attachment.filename || attachment.file?.name}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAttachment(index)}
                      className="text-foreground hover:text-[hsl(var(--destructive))] hover:bg-[hsl(var(--muted))]"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pin Option (Admin only) */}
          {canPin && postType === 'announcement' && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="pin"
                checked={isPinned}
                onCheckedChange={setIsPinned}
              />
              <Label htmlFor="pin" className="cursor-pointer text-foreground">
                Pin this announcement
              </Label>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t-[hsl(var(--card-border))]">
            <Button 
              variant="outline" 
              onClick={onClose} 
              disabled={loading}
              className="border-[hsl(var(--card-border))] text-foreground hover:bg-[hsl(var(--muted))]"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={loading || !content.trim()}
              className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-[hsl(var(--primary-foreground))]"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Posting...
                </>
              ) : (
                'Post'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
